import type DocumentFieldView from '@UI/Store/Documents/Views/DocumentFieldView';
import DocumentViewerContext from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Providers/DocumentViewerProvider/DocumentViewerContext';
import type ViewerFieldIndex from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Providers/DocumentViewerProvider/Helpers/ViewerFieldIndex';
import type { ShownChange } from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Providers/DocumentViewerProvider/Helpers/diffShownFields';
import type { ViewerListActionHandler } from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Providers/DocumentViewerProvider/ViewerListAction';
import type { ViewerHandlers } from '@verclam/docx-kit-editor';
import type { DocxViewerHandle } from '@verclam/docx-kit-react';
import * as React from 'react';

export type DocumentViewerContextValue = {
	/** Handle of the mounted viewer; `current` is `null` until the viewer is mounted. */
	readonly handleRef: React.RefObject<DocxViewerHandle | null>;
	/** 1 = 100 %. */
	readonly zoom: number;
	readonly minZoom: number;
	readonly maxZoom: number;
	/** Clamped to `[minZoom, maxZoom]`. */
	readonly setZoom: (zoom: number) => void;
	readonly fitToWidth: () => void;
	/** Guarded (a modified draft queues a discard request instead): selects and reveals the field, then opens its panel. */
	readonly goToField: (fieldKey: string) => void;
	/** Guarded likewise: the next / previous field to do (A11), from the open panel's field or from the top. */
	readonly goToAdjacentPending: (direction: 'next' | 'previous') => void;
	/** The field « Prochain à vérifier » would open; `null` when nothing remains to do. */
	readonly nextPendingField: DocumentFieldView | null;

	/** `true` while the zoom follows the viewport width (no manual zoom). */
	readonly isFitted: boolean;
	/** Viewer ↔ Dixit lookup of the current document: placement of a field key or a list key, list targets. */
	readonly fieldIndex: ViewerFieldIndex;
	/** `true` once the viewer instance exists (its `handle.fields()` is then meaningful). */
	readonly isViewerReady: boolean;
	/** `true` when the template cannot be read (fetch failure or viewer error): nothing can be shown or downloaded. */
	readonly isTemplateUnreadable: boolean;
	/**
	 * Where the document's list gestures (add, remove, move) go. Whoever owns the list actions assigns
	 * it from an effect and clears it on unmount; with no handler those gestures do nothing.
	 */
	readonly listActionHandlerRef: React.RefObject<ViewerListActionHandler | null>;
	/** Scrolls to and flashes a list element, without opening any panel. No-op when no field of it is displayed. */
	readonly revealItem: (listKey: string, index: number) => void;
	/** Scrolls to and flashes the passage a `diffShownFields` change designates. */
	readonly revealChange: (change: ShownChange) => void;

	/** The click and list handlers `DocumentViewer` hands to the library; they route to the panel and to `listActionHandlerRef`. */
	readonly viewerHandlers: Required<ViewerHandlers>;

	/** Internal to `DocumentViewer`: reports the width of the area the pages are laid in. */
	readonly reportViewportWidth: (width: number) => void;
	/** Internal to `DocumentViewer`: reports that `handle.fields()` now answers. */
	readonly reportViewerReady: () => void;
};

/** Throws outside `DocumentViewerProvider`. */
const useDocumentViewer = (): DocumentViewerContextValue => {
	const value = React.useContext(DocumentViewerContext);

	if (value === null) {
		throw new Error('useDocumentViewer must be used within DocumentViewerProvider.');
	}

	return value;
};

export default useDocumentViewer;
